import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_CSMS_API_ENDPOINT || 'http://localhost:8080';
const API_KEY = '8RoAJQlO6YXkDj2TWtSH7MP08LPCqXegxBP10zsc5wzgLr8ALfDLrsAP8LMb2A53IPXkz8iHeJbr8HA0LqootCmfuc1MUS2P2SBHrTLn1XKoylW1R4Qmtskz70hTCyIe';

// Create a separate axios instance for public API calls (no authentication required)
const publicAxios = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

/**
 * Get active transaction for a charger and connector
 * @param {string} chargerId - The charger ID
 * @param {string} connectorId - The connector ID
 * @returns {Promise<Object>} Active transaction details
 */
export const getActiveTransaction = async (chargerId, connectorId) => {
  try {
    const url = `/cms/v1/public/transaction/active?chargerId=${encodeURIComponent(chargerId)}&connectorId=${encodeURIComponent(connectorId)}`;
    const headers = {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json',
    };
    
    const response = await publicAxios({
      url,
      method: 'GET',
      headers,
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching active transaction:', error);
    
    // Handle different types of errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
    }
    
    if (error.response?.status === 404) {
      throw new Error('No active transaction found for this charger and connector.');
    }
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Authentication failed. Please contact support.');
    }
    
    if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error(error.message || 'Failed to fetch active transaction. Please try again.');
  }
};

/**
 * Stop an active transaction
 * @param {number} transactionId - The transaction ID to stop
 * @param {string} chargerId - The charger ID
 * @param {string} connectorId - The connector ID
 * @param {string} utrNumber - The UTR number used for the transaction
 * @returns {Promise<Object>} Stop transaction response
 */
export const stopTransaction = async (transactionId, chargerId, connectorId, utrNumber) => {
  try {
    const url = `/cms/v1/public/transaction/stop`;
    const headers = {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json',
    };
    
    const response = await publicAxios({
      url,
      method: 'POST',
      headers,
      data: {
        transactionId: transactionId,
        chargerId: chargerId,
        connectorId: connectorId,
        utrNumber: utrNumber
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error stopping transaction:', error);
    
    // Handle different types of errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
    }
    
    if (error.response?.status === 404) {
      throw new Error('Transaction not found.');
    }
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Authentication failed. Please contact support.');
    }
    
    if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error(error.message || 'Failed to stop transaction. Please try again.');
  }
};


/**
 * Get charger details including connector status
 * @param {string} chargerId - The charger ID
 * @returns {Promise<Object>} Charger details with connector status
 */
export const getChargerDetails = async (chargerId) => {
  try {
    const url = `/cms/v1/charger/${encodeURIComponent(chargerId)}`;
    const headers = {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json',
    };
    
    const response = await publicAxios({
      url,
      method: 'GET',
      headers,
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching charger details:', error);
    
    // Handle different types of errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
    }
    
    if (error.response?.status === 404) {
      throw new Error('Charger not found.');
    }
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Authentication failed. Please contact support.');
    }
    
    if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error(error.message || 'Failed to fetch charger details. Please try again.');
  }
};

/**
 * Send OTP to email for charging update notifications
 * @param {string} email - The email address to send OTP to
 * @param {number} transactionId - The active transaction ID
 * @returns {Promise<Object>} OTP send response
 */
export const sendOTPToEmail = async (email, transactionId) => {
  try {
    const url = `/cms/v1/public/notification/send-otp`;
    const headers = {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json',
    };
    
    const response = await publicAxios({
      url,
      method: 'POST',
      headers,
      data: {
        email: email,
        transactionId: transactionId
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error sending OTP:', error);
    
    // Handle different types of errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
    }
    
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Invalid email address or request.');
    }
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Authentication failed. Please contact support.');
    }
    
    if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error(error.message || 'Failed to send OTP. Please try again.');
  }
};

/**
 * Verify OTP and set notification for charging updates
 * @param {string} email - The email address
 * @param {string} otp - The OTP code
 * @param {number} transactionId - The transaction ID
 * @returns {Promise<Object>} OTP verification response
 */
export const verifyOTP = async (email, otp, transactionId) => {
  try {
    const url = `/cms/v1/public/notification/verify-otp`;
    const headers = {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json',
    };
    
    const response = await publicAxios({
      url,
      method: 'POST',
      headers,
      data: {
        email: email,
        otp: otp,
        transactionId: transactionId
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    
    // Handle different types of errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
    }
    
    if (error.response?.status === 400) {
      throw new Error(error.response?.data?.message || 'Invalid OTP or request. Please try again.');
    }
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      throw new Error('Authentication failed. Please contact support.');
    }
    
    if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    }
    
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    throw new Error(error.message || 'Failed to verify OTP. Please try again.');
  }
};

/**
 * Send OTP to phone number for stop transaction verification
 * @param {string} chargerId
 * @param {string} connectorId
 * @param {string} phone - 10-digit phone number
 * @param {string} countryCode - e.g. "+91"
 * @param {string} domain
 */
export const sendStopOTP = async (chargerId, connectorId, phone, countryCode, domain) => {
  try {
    const response = await publicAxios({
      url: `/cms/v1/otp/send`,
      method: 'POST',
      headers: { 'x-api-key': API_KEY, 'Content-Type': 'application/json' },
      data: { chargerId, connectorId, phone, countryCode, domain, action: 'RemoteStop' },
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message) throw new Error(error.response.data.message);
    throw new Error(error.message || 'Failed to send OTP. Please try again.');
  }
};

/**
 * Verify OTP and trigger remote stop
 * @param {string} phone - 10-digit phone number
 * @param {string} countryCode - e.g. "+91"
 * @param {string} otp - 6-digit OTP
 * @param {number} transactionId
 * @param {string} domain
 */
export const verifyStopOTP = async (phone, countryCode, otp, transactionId, domain) => {
  try {
    const response = await publicAxios({
      url: `/cms/v1/otp/verify`,
      method: 'POST',
      headers: { 'x-api-key': API_KEY, 'Content-Type': 'application/json' },
      data: { phone, countryCode, otp, action: 'RemoteStop', transactionId, domain },
    });
    return response.data;
  } catch (error) {
    if (error.response?.data?.message) throw new Error(error.response.data.message);
    throw new Error(error.message || 'Failed to verify OTP. Please try again.');
  }
};

export default {
  getActiveTransaction,
  stopTransaction,
  getChargerDetails,
  sendOTPToEmail,
  verifyOTP,
  sendStopOTP,
  verifyStopOTP,
};

